import { useSyncExternalStore } from "react";

/* ══════════════════════════════════════════════════════════════════
   „Zuletzt geöffnet" — was dieser Mensch auf diesem Gerät zuletzt
   aufgemacht hat (19.09.2026).

   Die Suche am Telefon ist dem Vorbild nachgebaut (OVIS,
   `TelefonSuche.jsx`), und dort steht unter dem Feld „Zuletzt geöffnet":
   der Einstieg auf dem leeren Schirm und der Rückweg nach einer Suche, die
   nichts war. OVIS führt den Verlauf auf dem Server; wir haben dafür keine
   Tabelle, und eine neue gehörte ins Datenmodell (CLAUDE.md, „Zusammenarbeit":
   das Datenmodell ist gesetzt).

   **Deshalb liegt er im Gerät** (`localStorage`) — und das ist für diesen
   Zweck kein Mangel: „Zuletzt" ist eine Bequemlichkeit je Gerät, kein
   Datenbestand. Geht der Eintrag verloren, steht die Liste leer da, und
   mehr passiert nicht. Getrennt je Benutzer, weil sich zwei Leute ein
   Telefon auf dem Hof teilen können.

   **Gemerkt wird, was eine Akte hat:** Kunde, Auftrag, Angebot, Gerät.
   Belege nicht — sie öffnen sich in der Belegsuche, und die ist kein Ort,
   an den man zurückkehrt.
   ══════════════════════════════════════════════════════════════════ */

export type Sorte = "kunden" | "auftraege" | "angebote" | "geraete" | "belege";

export type Zuletzt = {
  sorte: Exclude<Sorte, "belege">;
  /** Die Kennung, über die man wieder hinkommt — Kundennummer, Vorgangsnummer, Gerät. */
  schluessel: string;
  titel: string;
  /** Die leise Zeile darunter, schon zusammengesetzt. */
  unter: string;
  /** Millisekunden seit 1970 — nur zum Sortieren. */
  wann: number;
};

const HOECHSTENS = 12;
const VORSATZ = "machine-office:zuletzt:";

let benutzer = "";
let liste: Zuletzt[] = [];
const horcher = new Set<() => void>();

function lesen(wer: string): Zuletzt[] {
  try {
    const roh = localStorage.getItem(VORSATZ + wer);
    const daten = roh ? JSON.parse(roh) as unknown : [];
    return Array.isArray(daten) ? daten as Zuletzt[] : [];
  } catch {
    return [];
  }
}

function schreiben(): void {
  try {
    localStorage.setItem(VORSATZ + benutzer, JSON.stringify(liste));
  } catch {
    /* Ohne Speicher gilt die Liste bis zum Neuladen. */
  }
}

function melden(): void {
  for (const h of horcher) h();
}

/** Für wen gemerkt wird. Die Suche ruft das beim Zeichnen; es kostet nur beim Wechsel etwas. */
export function zuletztFuer(wer: string): void {
  if (wer === benutzer) return;
  benutzer = wer;
  liste = lesen(wer);
  melden();
}

/** Einen Eintrag nach vorn holen — oder neu anlegen. */
export function zuletztMerken(e: Omit<Zuletzt, "wann">): void {
  if (!benutzer) return;
  liste = [
    { ...e, wann: Date.now() },
    ...liste.filter((z) => !(z.sorte === e.sorte && z.schluessel === e.schluessel)),
  ].slice(0, HOECHSTENS);
  schreiben();
  melden();
}

export function useZuletzt(): Zuletzt[] {
  return useSyncExternalStore(
    (h) => { horcher.add(h); return () => { horcher.delete(h); }; },
    () => liste,
    () => liste,
  );
}
